import { useEffect, useState } from 'react';
import { Routes, Route, useLocation, useNavigate, NavLink as RouterNavLink } from 'react-router-dom';
import axios from 'axios';
import {
  Bell,
  Boxes,
  CreditCard,
  Crown,
  History,
  Link2,
  LogOut,
  MapPin,
  Menu,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Plug,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  User,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { API, toast } from '@/App';
import CreateDelivery from '@/components/vendeur/CreateDelivery';
import DeliveryList from '@/components/vendeur/DeliveryList';
import DeliveryDetails from '@/components/vendeur/DeliveryDetails';
import ImportedOrdersPanel from '@/components/vendeur/ImportedOrdersPanel';
import SubscribeExpress from '@/components/vendeur/SubscribeExpress';
import IntegrationsPanel from '@/components/vendeur/IntegrationsPanel';
import TrustCenterPanel from '@/components/vendeur/TrustCenterPanel';
import WalletPanel from '@/components/vendeur/WalletPanel';
import TrackingSearch from '@/components/shared/TrackingSearch';
import NotificationBell from '@/components/shared/NotificationBell';
import logoSidebar from '@/assets/logo-cn-nr-ps.png';
import { clearAuthSession } from '@/utils/authSession';
import {
  UI_ACTION_LABELS,
  UI_FEEDBACK_MESSAGES,
  UI_NAV_LABELS,
  UI_PAGE_TITLES,
  UI_ROLE_LABELS,
} from '@/constants/uiLabels';

const VendeurDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, in_progress: 0, delivered: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('vendor-sidebar-collapsed') === '1';
    } catch (error) {
      return false;
    }
  });

  useEffect(() => {
    loadUserData();
    loadStats();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('vendor-sidebar-collapsed', sidebarCollapsed ? '1' : '0');
    } catch (error) {
      // ignore localStorage errors
    }
  }, [sidebarCollapsed]);

  const loadUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);

      if (!response.data.is_validated) {
        toast.warning(UI_FEEDBACK_MESSAGES.accountPendingValidation);
      }
    } catch (_error) {
      console.warn('Impossible de charger le profil vendeur.');
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/deliveries`);
      const deliveries = response.data;

      setStats({
        total: deliveries.length,
        pending: deliveries.filter((delivery) => delivery.status === 'pending_payment').length,
        in_progress: deliveries.filter((delivery) =>
          ['paid', 'pickup_in_progress', 'in_transit', 'at_relay'].includes(delivery.status)
        ).length,
        delivered: deliveries.filter((delivery) => delivery.status === 'delivered').length,
      });
    } catch (_error) {
      console.warn('Impossible de charger les statistiques vendeur.');
    }
  };

  const handleLogout = () => {
    clearAuthSession();
    navigate('/');
    toast.success(UI_FEEDBACK_MESSAGES.seeYouSoon);
  };

  const navTitleMap = [
    { match: '/vendeur/create', title: UI_NAV_LABELS.newDelivery },
    { match: '/vendeur/tracking', title: UI_NAV_LABELS.tracking },
    { match: '/vendeur/history', title: UI_NAV_LABELS.history },
    { match: '/vendeur/wallet', title: UI_NAV_LABELS.wallet },
    { match: '/vendeur/integrations', title: UI_NAV_LABELS.integrations },
    { match: '/vendeur/imported-orders', title: UI_NAV_LABELS.importedOrders },
    { match: '/vendeur/trust', title: UI_NAV_LABELS.trustReviews },
    { match: '/account/settings', title: UI_NAV_LABELS.settings },
    { match: '/account/notifications', title: UI_NAV_LABELS.notificationPreferences },
    { match: '/account/addresses', title: UI_NAV_LABELS.savedAddresses },
    { match: '/vendeur/subscribe-express', title: 'Coli Noki Pro' },
    { match: '/vendeur/delivery/', title: UI_PAGE_TITLES.deliveryDetail },
    { match: '/affiliate/dashboard', title: UI_NAV_LABELS.affiliate },
    { match: '/vendeur', title: UI_NAV_LABELS.deliveries },
  ];

  const currentSectionTitle =
    navTitleMap.find((item) => location.pathname.startsWith(item.match))?.title || UI_NAV_LABELS.dashboard;

  const SidebarLink = ({ to, icon: Icon, children, end = false }) => (
    <RouterNavLink
      to={to}
      end={end}
      title={sidebarCollapsed ? children : undefined}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center rounded-lg border transition-colors ${
          sidebarCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'
        } ${
          isActive
            ? 'border-red-200 bg-red-50 text-red-700 shadow-sm'
            : 'border-transparent text-gray-700 hover:border-red-100 hover:bg-red-50'
        }`
      }
    >
      <Icon className="h-5 w-5 shrink-0 text-red-600" />
      {!sidebarCollapsed && <span>{children}</span>}
    </RouterNavLink>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 h-screen w-64 border-r border-gray-200 bg-white transition-all duration-300 ease-in-out lg:static lg:shrink-0
          ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex h-full flex-col">
          <div className={`${sidebarCollapsed ? 'p-3' : 'p-6'} border-b border-gray-200`}>
            <div className="flex items-center justify-between">
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : 'gap-3'}`}>
                <img src={logoSidebar} alt="Coli Noki" className={sidebarCollapsed ? 'h-10 w-10 object-contain' : 'h-12 w-28 object-contain object-left'} />
                {!sidebarCollapsed && (
                  <div>
                    <p className="text-xs text-gray-500">{UI_ROLE_LABELS.seller}</p>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden lg:inline-flex"
                  onClick={() => setSidebarCollapsed((current) => !current)}
                  title={sidebarCollapsed ? UI_ACTION_LABELS.openMenu : UI_ACTION_LABELS.collapseMenu}
                >
                  {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          <nav className={`min-h-0 flex-1 overflow-y-auto ${sidebarCollapsed ? 'space-y-1 p-2' : 'space-y-2 p-4'}`}>
            <SidebarLink to="/vendeur" icon={Package} end>
              {UI_NAV_LABELS.deliveries}
            </SidebarLink>
            <SidebarLink to="/vendeur/create" icon={Plus}>
              {UI_NAV_LABELS.newDelivery}
            </SidebarLink>
            <SidebarLink to="/vendeur/tracking" icon={Search}>
              {UI_NAV_LABELS.tracking}
            </SidebarLink>
            <SidebarLink to="/vendeur/history" icon={History}>
              {UI_NAV_LABELS.history}
            </SidebarLink>
            <SidebarLink to="/vendeur/wallet" icon={CreditCard}>
              {UI_NAV_LABELS.wallet}
            </SidebarLink>
            <SidebarLink to="/vendeur/integrations" icon={Plug}>
              {UI_NAV_LABELS.integrations}
            </SidebarLink>
            <SidebarLink to="/vendeur/imported-orders" icon={Boxes}>
              {UI_NAV_LABELS.importedOrders}
            </SidebarLink>
            <SidebarLink to="/vendeur/trust" icon={ShieldCheck}>
              {UI_NAV_LABELS.trustReviews}
            </SidebarLink>
            <SidebarLink to="/account/settings" icon={Settings}>
              {UI_NAV_LABELS.settings}
            </SidebarLink>
            <SidebarLink to="/account/notifications" icon={Bell}>
              {UI_NAV_LABELS.notificationPreferences}
            </SidebarLink>
            <SidebarLink to="/account/addresses" icon={MapPin}>
              {UI_NAV_LABELS.savedAddresses}
            </SidebarLink>
            <SidebarLink to="/affiliate/dashboard" icon={Link2} end>
              {UI_NAV_LABELS.affiliate}
            </SidebarLink>
            <SidebarLink to="/vendeur/subscribe-express" icon={Crown}>
              Coli Noki Pro
            </SidebarLink>
          </nav>

          <div className={`${sidebarCollapsed ? 'p-3' : 'p-4'} border-t border-gray-200`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'mb-3 justify-center' : 'mb-4 gap-3'}`}>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <User className="h-5 w-5 text-red-600" />
              </div>
              {!sidebarCollapsed && (
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{user?.full_name}</p>
                  <p className="truncate text-xs text-gray-500">{user?.email}</p>
                </div>
              )}
            </div>
            {!sidebarCollapsed && !user?.is_validated && (
              <div className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
                {UI_FEEDBACK_MESSAGES.pendingAccountBanner}
              </div>
            )}
            <Button
              data-testid="logout-btn"
              variant="outline"
              className={`w-full ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
              onClick={handleLogout}
              title={sidebarCollapsed ? UI_ACTION_LABELS.logout : undefined}
            >
              <LogOut className={`h-4 w-4 ${sidebarCollapsed ? '' : 'mr-2'}`} />
              {!sidebarCollapsed && UI_ACTION_LABELS.logout}
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="h-6 w-6" />
            </Button>
            <h2 className="text-2xl font-bold">{currentSectionTitle}</h2>
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <Routes>
            <Route
              path="/"
              element={
                <div>
                  <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                    <Card className="border-t-4 border-t-red-500">
                      <CardHeader className="pb-3">
                        <CardDescription>Total livraisons</CardDescription>
                        <CardTitle className="text-3xl">{stats.total}</CardTitle>
                      </CardHeader>
                    </Card>

                    <Card className="border-t-4 border-t-amber-500">
                      <CardHeader className="pb-3">
                        <CardDescription>En attente de paiement</CardDescription>
                        <CardTitle className="text-3xl">{stats.pending}</CardTitle>
                      </CardHeader>
                    </Card>

                    <Card className="border-t-4 border-t-blue-500">
                      <CardHeader className="pb-3">
                        <CardDescription>En cours</CardDescription>
                        <CardTitle className="text-3xl">{stats.in_progress}</CardTitle>
                      </CardHeader>
                    </Card>

                    <Card className="border-t-4 border-t-green-500">
                      <CardHeader className="pb-3">
                        <CardDescription>Livrées</CardDescription>
                        <CardTitle className="text-3xl">{stats.delivered}</CardTitle>
                      </CardHeader>
                    </Card>
                  </div>

                  <Card className="mb-8 bg-gradient-to-br from-red-500 to-cyan-500 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="mb-2 text-2xl font-bold">Créer une nouvelle livraison</h3>
                          <p className="mb-4 text-cyan-50">Organisez l’expédition de vos colis en quelques clics</p>
                          <Button
                            data-testid="create-delivery-quick-btn"
                            className="bg-white text-red-600 hover:bg-gray-100"
                            onClick={() => navigate('/vendeur/create')}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Nouvelle livraison
                          </Button>
                        </div>
                        <Package className="hidden h-32 w-32 opacity-20 lg:block" />
                      </div>
                    </CardContent>
                  </Card>

                  <DeliveryList onRefresh={loadStats} />
                </div>
              }
            />
            <Route path="/create" element={<CreateDelivery onSuccess={loadStats} />} />
            <Route path="/tracking" element={<TrackingSearch />} />
            <Route path="/history" element={<DeliveryList onRefresh={loadStats} />} />
            <Route path="/wallet" element={<WalletPanel />} />
            <Route path="/integrations" element={<IntegrationsPanel />} />
            <Route path="/imported-orders" element={<ImportedOrdersPanel />} />
            <Route path="/trust" element={<TrustCenterPanel />} />
            <Route path="/delivery/:id" element={<DeliveryDetails />} />
            <Route path="/subscribe-express" element={<SubscribeExpress />} />
          </Routes>
        </main>
      </div>

      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

export default VendeurDashboard;

